import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, Copy, CreditCard, Loader2, ArrowLeft, Calendar, Clock, AlertCircle, RefreshCw, QrCode } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Payment() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(30);
  const [error, setError] = useState(null);
  
  // Payment state
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [payment, setPayment] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans || []);
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Erro ao carregar planos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Poll for payment status when we have a pending payment
  useEffect(() => {
    if (!payment?.payment_id || paymentConfirmed) return;
    
    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/auth/check-subscription-payment/${payment.payment_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.status === 'approved') {
          setPaymentConfirmed(true);
          toast.success(response.data.message || 'Pagamento confirmado!');
          
          // Clear any cached user data to force fresh fetch
          localStorage.removeItem('user');
          
          // Force page reload to update subscription status in App.js
          // Using a full reload ensures the App.js checkSubscription runs fresh
          setTimeout(() => {
            window.location.replace('/dashboard');
          }, 2500);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    };
    
    // Check immediately
    checkPaymentStatus();
    
    // Then poll every 5 seconds
    const interval = setInterval(checkPaymentStatus, 5000);
    
    // Stop after 30 minutes
    const timeout = setTimeout(() => clearInterval(interval), 30 * 60 * 1000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [payment?.payment_id, paymentConfirmed]);

  const createPayment = async () => {
    setCreatingPayment(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/auth/create-subscription-payment`,
        { plan_days: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPayment(response.data);
      toast.success('PIX gerado! Escaneie o QR Code ou copie o código.');
    } catch (err) {
      console.error('Error creating payment:', err);
      const errorMsg = err.response?.data?.detail || 'Erro ao criar pagamento';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setCreatingPayment(false);
    }
  };

  const checkStatusManually = async () => {
    if (!payment?.payment_id) return;
    
    setCheckingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/auth/check-subscription-payment/${payment.payment_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'approved') {
        setPaymentConfirmed(true);
        toast.success(response.data.message || 'Pagamento confirmado!');
        
        // Clear any cached user data
        localStorage.removeItem('user');
        
        // Force page reload 
        setTimeout(() => {
          window.location.replace('/dashboard');
        }, 2500);
      } else {
        toast.info(response.data.message || 'Aguardando pagamento...');
      }
    } catch (err) {
      toast.error('Erro ao verificar status');
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getSelectedPlanDetails = () => {
    return plans.find(p => p.days === selectedPlan) || { days: 30, name: 'Mensal', price: 19.90 };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-violet-500 mx-auto mb-4" />
          <p className="text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Pagamento Confirmado!</h2>
            <p className="text-slate-400 mb-6">
              Sua assinatura foi renovada com sucesso. +{payment?.plan_days || 30} dias adicionados!
            </p>
            <p className="text-sm text-slate-500">
              Redirecionando para o Dashboard...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6" data-testid="payment-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-slate-400 hover:text-white mb-6 transition-colors"
          data-testid="btn-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </button>

        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Renovar Assinatura
          </h1>
          <p className="text-slate-400">
            Pague via PIX e sua assinatura será ativada automaticamente
          </p>
        </div>

        {error && (
          <Alert className="mb-6 border-red-500/50 bg-red-500/10">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Plan Selection */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Calendar className="h-5 w-5 text-violet-500" />
                Plano
              </CardTitle>
              <CardDescription>
                Assinatura mensal do MeuGestor
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.days}
                    onClick={() => !payment && setSelectedPlan(plan.days)}
                    disabled={!!payment}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedPlan === plan.days
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    } ${payment ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`plan-${plan.days}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-white">{plan.name}</span>
                        <p className="text-sm text-slate-400">{plan.days} dias</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-white">
                          {formatCurrency(plan.price)}
                        </span>
                        {plan.discount && (
                          <span className="block text-xs text-emerald-400 font-medium">
                            {plan.discount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {!payment && (
                <Button
                  onClick={createPayment}
                  disabled={creatingPayment}
                  className="w-full mt-6 bg-violet-600 hover:bg-violet-700"
                  data-testid="btn-create-payment"
                >
                  {creatingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Gerando PIX...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      Gerar QR Code PIX
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Payment Details / QR Code */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <CreditCard className="h-5 w-5 text-emerald-500" />
                Pagamento PIX
              </CardTitle>
              <CardDescription>
                {payment ? 'Escaneie o QR Code ou copie o código' : 'Gere o PIX para pagar'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!payment ? (
                <div className="text-center py-8">
                  <QrCode className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <p className="text-slate-400">
                    Clique em &quot;Gerar QR Code PIX&quot; para começar
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Payment Summary */}
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400">Plano:</span>
                      <span className="text-white font-medium">{getSelectedPlanDetails().name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Valor:</span>
                      <span className="text-2xl font-bold text-violet-400">
                        {formatCurrency(payment.amount)}
                      </span>
                    </div>
                  </div>

                  {/* QR Code */}
                  {payment.qr_code_base64 ? (
                    <div className="flex flex-col items-center">
                      <div className="bg-white p-4 rounded-lg mb-4">
                        <img 
                          src={`data:image/png;base64,${payment.qr_code_base64}`}
                          alt="QR Code PIX"
                          className="w-48 h-48"
                          data-testid="pix-qr-code"
                        />
                      </div>
                      <p className="text-sm text-slate-400 text-center mb-4">
                        Escaneie o QR Code com o app do seu banco
                      </p>
                    </div>
                  ) : (
                    <Alert className="border-amber-500/50 bg-amber-500/10">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <AlertDescription className="text-amber-400">
                        QR Code não disponível. Use o código PIX abaixo.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* PIX Code (Copia e Cola) */}
                  {payment.qr_code && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-slate-400">Código PIX (Copia e Cola):</label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(payment.qr_code, 'Código PIX')}
                          className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
                          data-testid="btn-copy-pix"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copiar código
                        </Button>
                      </div>
                      <div 
                        className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 max-h-32 overflow-y-auto"
                        onClick={() => copyToClipboard(payment.qr_code, 'Código PIX')}
                      >
                        <p 
                          className="text-white text-xs font-mono break-all cursor-pointer select-all"
                          data-testid="pix-code-text"
                        >
                          {payment.qr_code}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 text-center">
                        Clique no código acima para copiar
                      </p>
                    </div>
                  )}

                  {/* Status and Check */}
                  <div className="pt-4 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock className="w-4 h-4" />
                        PIX válido por 24 horas
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                        <span className="text-sm text-amber-400">Aguardando pagamento</span>
                      </div>
                    </div>

                    <Button
                      onClick={checkStatusManually}
                      disabled={checkingStatus}
                      variant="outline"
                      className="w-full border-zinc-700 text-slate-300 hover:bg-zinc-800"
                      data-testid="btn-check-status"
                    >
                      {checkingStatus ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Já paguei, verificar status
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center mt-3">
                      O status é verificado automaticamente a cada 5 segundos.
                      Após o pagamento, sua assinatura será ativada instantaneamente.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="mt-6 bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Como funciona?</h3>
                <p className="text-sm text-slate-400">
                  1. Gere o QR Code PIX acima<br/>
                  2. Escaneie com o app do seu banco ou copie o código<br/>
                  3. Após o pagamento, os dias serão <strong className="text-white">somados automaticamente</strong> à sua assinatura atual
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
